import { AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useAppLock } from '@/contexts/AppLockContext';
import { useDemo } from '@/contexts/DemoContext';
import AppLockScreen from '@/components/AppLockScreen';
import { Loader2 } from 'lucide-react';

/**
 * AppLockGate - Controls access to the app based on lock state
 * 
 * Shows the lock screen when:
 * - User is authenticated AND
 * - App lock is enabled AND
 * - App is currently locked
 * 
 * Shows loading spinner while checking lock state.
 * In DEMO MODE: Skip all loading/locking - demo doesn't need auth lock
 */
const AppLockGate = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isLocked, appLockEnabled, checkingLock } = useAppLock();
  const { isDemoMode, loading: demoLoading } = useDemo();

  // In demo mode, skip all loading and locking
  if (isDemoMode) {
    return children;
  }

  // Show loading while checking auth or lock state (but not if demo mode is still loading)
  if (authLoading || (checkingLock && !demoLoading)) {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-sky-50 via-white to-rose-50 flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-sky-500 animate-spin" />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Show lock screen if authenticated and locked
  const shouldShowLockScreen = isAuthenticated && appLockEnabled && isLocked;

  return (
    <>
      <AnimatePresence mode="wait">
        {shouldShowLockScreen && <AppLockScreen key="lock-screen" />}
      </AnimatePresence>
      
      {/* Always render children but hide them when locked */}
      <div style={{ display: shouldShowLockScreen ? 'none' : 'block' }}>
        {children}
      </div>
    </>
  );
};

export default AppLockGate;
